import { Col, Flex, Row, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import MapComponent, { MapData } from '../../components/map/MapComponent';
import './LocationPage.scss';
import ModalState from '../../components/map/ModalState';
import ModalEditState from '../../components/map/ModalEditState';
import { useTranslation } from '../../i18n/i18n';
import { locationApi } from '../../api/location.api';
import { installationStateApi } from '../../api/installation-state.api';
import { InstallationState } from '../../gen/proto/api/gateway/v1/installation_state_pb';
import { installationStateLabel } from '../../utils/mapTranslationEnums';
import useAuthStore from '../../stores/auth.store';
import useTenantStore from '../../stores/tenant.store';

const { Text } = Typography;

const resolveMarkerColor = (state: InstallationState | undefined | null): string => {
  return state?.color || '#cccccc';
};

const LocationPage = () => {
  const { t } = useTranslation('location');
  const [mapMarkers, setMapMarkers] = useState<MapData[]>([]);
  const [status, setStatus] = useState<{ uuid: string; name: string; color: string }[]>([]);
  const [statusFull, setStatusFull] = useState<InstallationState[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await locationApi.getAllLocationsWithInstallationWorstState();
        const mapped = response.locations.map((item) => ({
          location_id: item.location!.uuid,
          latitude: item.location!.latitude,
          longitude: item.location!.longitude,
          color: resolveMarkerColor(item.state),
        }));
        setMapMarkers(mapped);
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error fetching location page');
      }
    };
    fetch();
  }, [selectedTenant]);

  const fetchStatuses = useCallback(async () => {
    const response = await installationStateApi.getInstallationStateList();
    setStatusFull(response);
    const mapped = response.map((s) => ({
      uuid: s.uuid,
      name: installationStateLabel(s),
      color: s.color,
    }));
    setStatus(mapped);
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const formatLastUpdate = (date: Date | null) => {
    if (!date) return t('never');
    return date.toLocaleTimeString();
  };

  return (
    <Flex vertical className={'LocationPage'}>
      <Space size={'middle'} className={'LocationPage__header'}>
        <EnvironmentOutlined className={'LocationPage__icon'} />
        <Typography.Title level={1} className={'Title'}>
          {t('title')}
        </Typography.Title>

        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
          {t('last_update')} {formatLastUpdate(lastUpdate)}
        </Typography.Text>
      </Space>
      <div className={'LocationPage__toolbar'}>
        <Row gutter={[16, 8]} className={'LocationPage__toolbar__table'}>
          {status.map((item) => (
            <Col key={item.uuid} span={8} className={'LocationPage__toolbar-item'}>
              <div style={{ backgroundColor: item.color }} className={'LocationPage__toolbar-item-icon'} />
              <Text>{item.name}</Text>
            </Col>
          ))}
        </Row>
        {isSuperAdmin && (
          <div className={'LocationPage__toolbar__modalButtons'}>
            <ModalState onSaved={fetchStatuses} />
            <ModalEditState fullStates={statusFull} onSaved={fetchStatuses} />
          </div>
        )}
      </div>

      <div className={'LocationPage__map'}>
        <MapComponent mapData={mapMarkers} />
      </div>
    </Flex>
  );
};

export default LocationPage;
